import React from "react"

import PropTypes from "prop-types"
import { useHref } from "react-router-dom"

import { useRouter } from "../../hooks/useRouter"
import { isKeyboardClickEvent } from "../../utils/accessibilityUtil"
import { getDataTestId } from "../../utils/stringUtil"

import "./link.scss"

export const LINK_VARIANTS = {DEFAULT: "default", DARK: "dark", LIGHT: "light"}

// Un lien qui prend en compte les propriétés dans cet ordre : 1 -> onClick, 2 -> route, 3 -> href
const Link = ({
    ariaLabel = undefined,
    children,
    className = null,
    dataTestId = null,
    disabled = false,
    href = undefined,
    onClick = undefined,
    openInNewTab = false,
    route = undefined,
    variant = LINK_VARIANTS.DEFAULT,
}) => {
    const {navigate} = useRouter()
    const url = useHref(route)

    // Si nous avons un onClick ou une route, on utilise un button
    const isButton = onClick || route

    const handleClick = e => {
        if (disabled) {
            e.preventDefault()
            return
        }

        if (onClick) {
            e.preventDefault()
            onClick(e)
            return
        }

        if (route) {
            if (openInNewTab && url) {
                window.open(url, "_blank", "noopener,noreferrer")
            } else {
                navigate(route)
            }
        }
    }

    const handleKeyDown = e => {
        if (isKeyboardClickEvent(e)) {
            handleClick(e)
        }
    }

    const getAriaLabel = () => {
        if (ariaLabel) {
            return openInNewTab ? `${ariaLabel} (s'ouvre dans un nouvel onglet)` : ariaLabel
        }
        return openInNewTab ? `${children} (s'ouvre dans un nouvel onglet)` : undefined
    }

    const commonProps = {
        "aria-disabled": disabled,
        "aria-label": getAriaLabel(),
        className: `link-component variant-${variant}${className ? " " + className : ""}${disabled ? " disabled" : ""}`,
        "data-testid": dataTestId || getDataTestId(children, "lien"),
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        role: isButton ? "button" : "link",
        tabIndex: disabled ? -1 : 0,
    }

    if (isButton) {
        return (
            <button disabled={disabled} type="button" {...commonProps}>
                {children}
            </button>
        )
    }

    return (
        <a
            href={disabled ? undefined : href || "#"}
            rel={openInNewTab ? "noreferrer noopener" : undefined}
            target={openInNewTab ? "_blank" : undefined}
            {...commonProps}
        >
            {children}
        </a>
    )
}

Link.propTypes = {
    ariaLabel: PropTypes.string,
    children: PropTypes.any.isRequired,
    className: PropTypes.string,
    dataTestId: PropTypes.string,
    disabled: PropTypes.bool,
    href: PropTypes.string,
    onClick: PropTypes.func,
    openInNewTab: PropTypes.bool,
    route: PropTypes.string,
    variant: PropTypes.oneOf(Object.values(LINK_VARIANTS)),
}

export default Link
